import React, { useState, useEffect, useRef, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DEFAULT_MIN = new Date('2025-11-01T00:00:00');

/** YYYY-MM-DD string → Date (local timezone, no UTC shift) */
const strToDate = (str) => (str ? new Date(str + 'T00:00:00') : null);

/** Date → YYYY-MM-DD string (local timezone) */
const dateToStr = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Masked input: digits-only, auto-inserts "/" after DD and MM.
 * Displays as DD/MM/YYYY. Blocks all non-digit keys.
 */
const MaskedInput = forwardRef(function MaskedInput(
  { value, onClick, placeholder, className, disabled, onDateChange, minDate, maxDate },
  ref
) {
  const [display, setDisplay] = useState(value || '');
  const typing = useRef(false);
  const isDeleting = useRef(false);

  // Sync display from external value (calendar pick) when not typing
  useEffect(() => {
    if (!typing.current) {
      setDisplay(value || '');
    }
  }, [value]);

  const handleKeyDown = (e) => {
    const passthrough = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];
    isDeleting.current = e.key === 'Backspace' || e.key === 'Delete';
    if (!/^\d$/.test(e.key) && !passthrough.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    typing.current = true;
    const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
    let fmt = raw.slice(0, 2);
    if (raw.length > 2) fmt += '/' + raw.slice(2, 4);
    if (raw.length > 4) fmt += '/' + raw.slice(4, 8);
    // Auto-append slash after DD (2 digits) and MM (4 digits) when typing forward
    if (!isDeleting.current && (raw.length === 2 || raw.length === 4)) fmt += '/';
    setDisplay(fmt);

    if (fmt.length === 10) {
      const [dd, mm, yyyy] = fmt.split('/');
      let d = new Date(+yyyy, +mm - 1, +dd);
      if (!isNaN(d.getTime())) {
        if (minDate && d < minDate) d = minDate;
        if (maxDate && d > maxDate) d = maxDate;
        onDateChange(d);
      }
    } else if (fmt.length === 0) {
      onDateChange(null);
    }
  };

  const handleBlur = () => {
    typing.current = false;
    // Reset incomplete input to last valid value
    if (display.length > 0 && display.length < 10) {
      setDisplay(value || '');
    }
  };

  return (
    <input
      ref={ref}
      value={display}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      onBlur={handleBlur}
      placeholder={placeholder || 'DD/MM/YYYY'}
      className={className}
      disabled={disabled}
    />
  );
});

/**
 * Drop-in date picker with masked input.
 *
 * Interface (ใช้ string ตรง ๆ ไม่ต้องแปลง Date ใน component):
 *   value        — YYYY-MM-DD string (หรือ '' สำหรับ empty)
 *   onChange(str) — รับ YYYY-MM-DD string (หรือ '' เมื่อ clear)
 *   minDate      — Date object (default: 01/11/2025)
 *   maxDate      — Date object (default: วันนี้)
 *   className    — CSS class ของ input
 *   placeholderText — placeholder text
 *   disabled     — boolean
 *
 * ตัวอย่างการใช้งาน:
 *   <MaskedDatePicker value={dateFrom} onChange={setDateFrom} className="..." />
 */
const MaskedDatePicker = ({
  value,
  onChange,
  className,
  minDate = DEFAULT_MIN,
  maxDate = new Date(),
  disabled,
  placeholderText,
}) => {
  const handleChange = (date) => onChange(dateToStr(date));

  return (
    <DatePicker
      selected={strToDate(value)}
      onChange={handleChange}
      dateFormat="dd/MM/yyyy"
      customInput={
        <MaskedInput
          onDateChange={handleChange}
          placeholder={placeholderText || 'DD/MM/YYYY'}
          className={className}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
        />
      }
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
    />
  );
};

export default MaskedDatePicker;
